/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect, useMemo, useState } from 'react';
import type { StaffClass, StaffGet, StaffTeacher, StaffTeacherClass, TeacherClassesAdd } from '@whizz/api-model/dist/@types';
import type { QueryObserverResult } from 'react-query';
import type { CellProps, Column } from 'react-table';
import type { ThemeProps } from 'styled-components';
import type { ThemeType } from '../../../../../util/types';
import type { DataToEdit } from './AddEditInitialiseStaffModal';
import type { ToggleModalProps } from '../../../../../components/modal/types';
import type { AdditionalHeaderCellProps } from '../../../../../components/table/types';
import type Teacher from '@whizz/api-client/dist/@types/model/Teacher';
import { Button, Choice, Loader, Table } from '../../../../../components';
import { Edit, TableHeader } from './StaffTable.components';
import styled, { createGlobalStyle } from 'styled-components';
import { repository } from '@whizz/api-client';
import { useMutation } from 'react-query';
import { splitPhone } from '../../../../../util/phone';
import { DateTime } from 'luxon';
import { Translate, useI18n } from '@whizz/react-i18n';
import { useIdentity } from '@whizz/react-identity';
import { useModal } from '../../../../../hooks/useModal';
import { ModalFooter } from '../../../../../components/modal/Modal';
import { CancelButtonModal } from '../../../../../global';

const LETTER_WIDTH = 11;
const COLUMN_WIDTHS = {
  'admin': 50,
  'name': 200,
  'allNone': 50,
  'class': 32,
};

const GlobalStyle = createGlobalStyle<ThemeProps<ThemeType> & { $height: number }>`
  #staff {
    thead {
    z-index: 500;
      tr:first-child {
        height: ${(props) => (props.$height * LETTER_WIDTH)}px;
      }
    },
    th {
      font-style: normal;
      font-size: 1.25rem;
    }
    [data-id^='col'] {
      > div {
        flex-direction: column;
        justify-content: flex-end;
        height: 100%;
      }
    }
    [data-id='col_name'] {
      > div {
        align-items: flex-start;
      }
    }
    [data-id='col_allNone'] {
      width: ${COLUMN_WIDTHS.allNone}px;
      min-width: ${COLUMN_WIDTHS.allNone}px;
      max-width: ${COLUMN_WIDTHS.allNone}px;
    }
    [data-id='col_admin'] {
      width: ${COLUMN_WIDTHS.admin}px;
      min-width: ${COLUMN_WIDTHS.admin}px;
      max-width: ${COLUMN_WIDTHS.admin}px;
    }

    [data-id^='col_class'] {
      width: ${COLUMN_WIDTHS.class}px;
      min-width: ${COLUMN_WIDTHS.class}px;
      max-width: ${COLUMN_WIDTHS.class}px;
    }
    [data-id='col_name'],
    [data-column='cell_name'] {
      width: ${COLUMN_WIDTHS.name}px;
      min-width: ${COLUMN_WIDTHS.name}px;
      max-width: ${COLUMN_WIDTHS.name}px;
      left: ${COLUMN_WIDTHS.admin}px !important;
    }
    [data-id='col_allNone'],
    [data-column='cell_allNone'] {
      left: ${COLUMN_WIDTHS.admin + COLUMN_WIDTHS.name}px !important;
    }
    [data-id='col_admin'],{
      font-weight: bold;
    }
    [data-id='col_allNone'],
    [data-column='cell_allNone'],
    [data-id='col_admin'],
    [data-column='cell_admin'],
    [data-column='cell_emailSubscribe'] {
      padding-left: 30px;
      padding-right: 30px;
    }
    [data-column='cell_admin'],
    [data-column='cell_emailSubscribe'],
    [data-column='cell_allNone'],
    [data-column^='cell_class'] {
      > div {
        display: flex;
        justify-content: center;
      }
    }
    [data-sticky-last-left-td="true"] {
      border-left: 1px solid ${(props) => props.theme.colors.muted};
      border-right: 1px solid ${(props) => props.theme.colors.muted};
    }
`;

type ClassPermission = 'none' | 'view' | 'email';



const IconButton = styled('button')`
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const EyeIcon = ({ active }: { active: boolean }) => (
  <svg width="26" height="16" viewBox="0 0 22 14" aria-hidden="true">
    <path
      d="M11 0.75C6.2 0.75 2.3 3.55 0.75 7c1.55 3.45 5.45 6.25 10.25 6.25S19.7 10.45 21.25 7C19.7 3.55 15.8 0.75 11 0.75Z"
      fill={active ? '#76B900' : '#C9CDD4'}
    />
    <circle cx="11" cy="7" r="3" fill={active ? '#FFFFFF' : '#F4F5F7'} />
    <circle cx="11" cy="7" r="1.5" fill={active ? '#2E2E2E' : '#9AA0A6'} />
  </svg>
);

const MailIcon = () => (
  <svg width="26" height="19" viewBox="0 0 22 16" aria-hidden="true">
    <rect x="1" y="2" width="20" height="12" rx="2" fill="#9B5CF6" />
    <path d="M2.5 4.2L11 9.3L19.5 4.2" stroke="#FFFFFF" strokeWidth="1.6" fill="none" />
  </svg>
);

const PopoverWrap = styled('div')`
  position: relative;
  display: inline-flex;
`;

const PopoverMenu = styled('div')`
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 400;

  background: white;
  border: 1px solid rgba(0,0,0,0.12);
  border-radius: 8px;
  box-shadow: 0 10px 24px rgba(0,0,0,0.18);
  padding: 6px;
  min-width: 180px;
`;

const PopoverItem = styled('button')<{ $active?: boolean }>`
  width: 100%;
  border: 0;
  background: ${(p) => (p.$active ? 'rgba(118,185,0,0.08)' : 'transparent')};
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: left;

  &:hover {
    background: rgba(0,0,0,0.06);
  }
`;

const PopoverLabel = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 2px;

  .title {
    font-size: 12px;
    font-weight: 600;
    line-height: 1.1;
  }

  .desc {
    font-size: 11px;
    opacity: 0.75;
    line-height: 1.1;
  }
`;

const LegendWrap = styled('div')`
  display: flex;
  flex-wrap: nowrap;
  gap: 16px;
  align-items: center;
  white-space: nowrap;
  overflow-x: auto;
`;

const LegendItem = styled('div')`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  line-height: 1.4;
`;

const LegendText = styled('span')`
  white-space: nowrap;
`;

const LegendBar = styled('div')`
  position: sticky;
  bottom: 0;
  width: 100%;
  background: white;
  z-index: 10;
  padding: 16px 16px 16px 363px;
`;

const PermissionCell = ({
  value,
  onChange,
  teacherId,
  teacherName,
  classId,
  className,
}: {
  value: ClassPermission;
  onChange: (next: ClassPermission) => void;
  teacherId: number;
  teacherName: string;
  classId: number;
  className: string;
}) => {
  const { translate } = useI18n();
  const [ open, setOpen ] = React.useState(false);
  const wrapRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!open) return;

    const onDocMouseDown = (e: MouseEvent) => {
      const el = wrapRef.current;
      if (!el) return;
      if (!el.contains(e.target as Node)) setOpen(false);
    };

    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [ open ]);

  React.useEffect(() => {
    if (!open) return;

    requestAnimationFrame(() => {
      wrapRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    });
  }, [ open ]);

  const choose = async (next: ClassPermission) => {
    onChange(next);
    setOpen(false);

    onChange(next);
    setOpen(false);
    if (next === 'none') {
      await repository.TeacherClassRepository.deleteTeacherClass({ teacherId, classId });
    }

    if (next === 'view') {
      await repository.NewTeacherRepository.updateIndividualSubscriptionWc({ teacherId, wantsToSubscribe: false, classId });
    }

    if (next === 'email') {
      await repository.NewTeacherRepository.updateIndividualSubscriptionWc({ teacherId, wantsToSubscribe: true, classId });
    }


  };

  return (
    <PopoverWrap ref={wrapRef}>
      <IconButton
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={translate('pages.ROSTER_MANAGEMENT.STAFF.EMAIL_SUBSCRIBE_MODAL.CHANGE_PERMISSION')}
      >
        {value === 'email' ? <MailIcon /> : <EyeIcon active={value === 'view'} />}
      </IconButton>

      {open ? (
        <PopoverMenu role="menu">
          <PopoverItem type="button" onClick={() => choose('none')} $active={value === 'none'}>
            <EyeIcon active={false} />
            <PopoverLabel>
              <div className="title"> {translate('pages.ROSTER_MANAGEMENT.STAFF.EMAIL_SUBSCRIBE_MODAL.NONE')}</div>
              <div className="desc">{translate('pages.ROSTER_MANAGEMENT.STAFF.EMAIL_SUBSCRIBE_MODAL.CANT_VIEW')}</div>
            </PopoverLabel>
          </PopoverItem>

          <PopoverItem type="button" onClick={() => choose('view')} $active={value === 'view'}>
            <EyeIcon active={true} />
            <PopoverLabel>
              <div className="title">{translate('pages.ROSTER_MANAGEMENT.STAFF.EMAIL_SUBSCRIBE_MODAL.VIEW')}</div>
              <div className="desc">{translate('pages.ROSTER_MANAGEMENT.STAFF.EMAIL_SUBSCRIBE_MODAL.CAN_VIEW')}</div>
            </PopoverLabel>
          </PopoverItem>

          <PopoverItem type="button" onClick={() => choose('email')} $active={value === 'email'}>
            <MailIcon />
            <PopoverLabel>
              <div className="title">{translate('pages.ROSTER_MANAGEMENT.STAFF.EMAIL_SUBSCRIBE_MODAL.VIEWWITHEMAIL')}</div>
              <div className="desc">{translate('pages.ROSTER_MANAGEMENT.STAFF.EMAIL_SUBSCRIBE_MODAL.VIEWWITHEMAILTIP')}</div>
            </PopoverLabel>
          </PopoverItem>
        </PopoverMenu>
      ) : null}
    </PopoverWrap>
  );
};

const StaffTable = ({ onEdit, newStaff, staffQuery }: StaffTableProps): JSX.Element => {
  const [ formattedStaff, setFormattedStaff ] = useState<StaffTableItem[] | null>(null);
  const { translate } = useI18n();
  const { me } = useIdentity();
  const user = (me as Teacher);

  const [ confirmWeeklySubscriptionModal, toggleWeeklySubscriptionModal, setWeeklySubscriptionModalProps ] = useModal((props: ConfirmWeeklySubscriptionProps) => (
    <ConfirmWeeklySubscription {...props} />
  ));

  useEffect(() => {
    if (staffQuery.isRefetching) {
      setFormattedStaff(null);
    } else if (staffQuery.isSuccess) {
      setFormattedStaff(formatData(staffQuery.data));
    }
  }, [ staffQuery.isSuccess, staffQuery.isRefetching, staffQuery.data ]);

  const updateStaffPropsById = (id: number, updateStaff: (teacher: StaffTableItem) => void) => {
    setFormattedStaff((prev) => {
      const newFormattedStaff = [ ...(prev ?? []) ];

      const teacherIndex = newFormattedStaff.findIndex((teacher) => teacher.id === id);

      newFormattedStaff[teacherIndex] = {
        ...newFormattedStaff[teacherIndex],
      };

      updateStaff(newFormattedStaff[teacherIndex]);


      newFormattedStaff[teacherIndex].updatedAt = DateTime.now().toISO({ includeOffset: false });

      return newFormattedStaff;
    });
  };

  const mutations = {
    isAdmin: useMutation(
      repository.NewTeacherRepository.updateAdminRights,
      {
        onSuccess: (data, { teacherId, newAdminValue }) => {
          updateStaffPropsById(
            teacherId,
            (teacher) => {
              teacher.admin = {
                loading: false,
                checked: newAdminValue,
              };
            },
          );
        },
        onError: (error, { teacherId }) => {
          updateStaffPropsById(
            teacherId,
            (teacher) => {
              teacher.admin = {
                ...teacher.admin,
                loading: false,
              };
            },
          );
        },
      },
    ),
    emailSubscribe: useMutation(
      repository.NewTeacherRepository.updateSubscriptionWc,
      {
        onSuccess: (data, { teacherId, wantsToSubscribe }) => {
          updateStaffPropsById(
            teacherId,
            (teacher) => {
              teacher.emailSubscribe = {
                loading: false,
                checked: wantsToSubscribe,
              };
            },
          );
        },
        onError: (error, { teacherId }) => {
          updateStaffPropsById(
            teacherId,
            (teacher) => {
              teacher.emailSubscribe = {
                ...teacher.emailSubscribe,
                loading: false,
              };
            },
          );
        },
      },
    ),
    teacherClassAdd: useMutation(
      repository.TeacherClassRepository.addTeacherClass,
      {
        onSuccess: (data, { teacherId, classId }) => {
          updateStaffPropsById(
            teacherId,
            (teacher) => {
              teacher.classes[classId] = {
                permission: 'view',
                loading: false,
              };
              teacher.allNone = {
                loading: false,
              };
            },
          );
        },
        onError: (error, { teacherId, classId }) => {
          updateStaffPropsById(
            teacherId,
            (teacher) => {
              teacher.classes[classId] = {
                ...teacher.classes[classId],
                loading: false,
              };
              teacher.allNone = {
                loading: false,
              };
            },
          );
        },
      },
    ),
    teacherClassesAdd: useMutation(({ teacherId, classesIds }: TeacherClassesAdd ) => repository.TeacherClassRepository.addTeacherClasses({ teacherId, classesIds }),
      {
        onSuccess: (data, { teacherId, classesIds }) => {
          updateStaffPropsById(
            teacherId,
            (teacher) => {
              classesIds.forEach((classId) => {
                teacher.classes[classId] = {
                  permission: 'view',
                  loading: false,
                };
              });
              teacher.allNone = {
                loading: false,
              };
            },
          );
        },
        onError: (data, { teacherId }) => {
          updateStaffPropsById(
            teacherId,
            (teacher) => {
              teacher.allNone = {
                loading: false,
              };
            },
          );
        },
      },
    ),
    teacherClassesDelete: useMutation(({ teacherId }: { teacherId: number } ) => repository.TeacherClassRepository.deleteTeacherClasses({ teacherId }),
      {
        onSuccess: (data, { teacherId }) => {
          updateStaffPropsById(
            teacherId,
            (teacher) => {
              Object.keys(teacher.classes).forEach((classId) => {
                teacher.classes[Number(classId)] = {
                  permission: 'none',
                  loading: false,
                };
              });
              teacher.allNone = {
                loading: false,
              };
            },
          );
        },
        onError: (data, { teacherId }) => {
          updateStaffPropsById(
            teacherId,
            (teacher) => {
              teacher.allNone = {
                loading: false,
              };
            },
          );
        },
      },
    ),
    teacherClassDelete: useMutation(
      repository.TeacherClassRepository.deleteTeacherClass,
      {
        onSuccess: (data, { teacherId, classId }) => {
          updateStaffPropsById(
            teacherId,
            (teacher) => {
              teacher.classes[classId] = {
                permission: 'none',
                loading: false,
              };
              teacher.allNone = {
                loading: false,
              };
            },
          );
        },
        onError: (error, { teacherId, classId }) => {
          updateStaffPropsById(
            teacherId,
            (teacher) => {
              teacher.classes[classId] = {
                ...teacher.classes[classId],
                loading: false,
              };
              teacher.allNone = {
                loading: false,
              };
            },
          );
        },
      },
    ),
  };

  const areThereSomeAdmins = formattedStaff && 1 < formattedStaff.filter((teacher) => teacher.isAdmin).length;



  const isLoadingData = (classes: StaffTableItem['classes']): boolean => {
    return !!Object.entries(classes).some((classObj) => classObj[1].loading);
  };

  const isAllChecked = (classes: StaffTableItem['classes']): boolean => {
    const someNone = Object.values(classes).some((c) => c.permission === 'none');
    return !someNone;
  };



  const columns: (Column<StaffTableItem> & AdditionalHeaderCellProps)[] = useMemo(() => [
    {
      id: 'admin',
      Header: <TableHeader title={translate('pages.ROSTER_MANAGEMENT.STAFF.ADMIN')} />,
      accessor: (originalRow) => originalRow.admin.checked ? 1 : 0,
      sticky: 'left',
      Cell: (cell: CellProps<StaffTableItem>) => (
        !cell.row.original.admin.loading
          ? (
            <Choice
              id={`admin${cell.row.original.id}`}
              checked={cell.value}
              readOnly={!areThereSomeAdmins}
              disabled={cell.row.original.id === me?.id}
              onChange={(event) => updateIsAdmin(cell.row.original.id, event.target.checked)}
            />
          )
          : <Loader disableLabel smallSize={true} />
      ),
    },

    {
      id: 'name',
      accessor: 'onEditParams',
      sticky: 'left',
      Cell: (cell: CellProps<StaffTableItem>) => {
        const { firstName, lastName } = cell.row.original;
        const name =  `${firstName} ${lastName}`;
        return <Edit label={name} onClick={onEdit ? () => onEdit(cell.row.original.onEditParams) : null} />;
      },
    },
    {
      id: 'allNone',
      Header: <TableHeader title={translate('pages.ROSTER_MANAGEMENT.STAFF.ALL_NONE')} />,
      sticky: 'left',
      Cell: (cell: CellProps<StaffTableItem>) => (
        !cell.row.original.allNone.loading
          ? (
            <Choice
              checked={isAllChecked(cell.row.original.classes)}
              disabled={isLoadingData(cell.row.original.classes)}
              onChange={(event) => updateTeacherClasses(cell.row.original.id, event.target.checked)}
            />
          )
          : <Loader disableLabel smallSize={true} />
      ),
    },
    ...staffQuery.data!.classes.map((classObj: StaffClass) => ({
      id: `class${classObj.id}`,
      Header: <TableHeader title={classObj.name} />,
      accessor: (row: StaffTableItem) => row.classes[classObj.id].permission,
      title: translate('components.TABLES.SORT_BY', { sort: classObj.name }),
      Cell: (cell: CellProps<StaffTableItem>) => {
        const teacherId = cell.row.original.id;
        const classId = classObj.id;

        if (cell.row.original.classes[classId].loading) {
          return <Loader disableLabel smallSize={true} />;
        }

        return (
          <PermissionCell
            value={cell.row.original.classes[classId].permission}
            teacherId={teacherId}
            teacherName={`${cell.row.original.firstName} ${cell.row.original.lastName ?? ''}`.trim()}
            classId={classId}
            className={classObj.name}
            onChange={(next) => {
              updateStaffPropsById(teacherId, (teacher) => {
                teacher.classes[classId].permission = next;
              });
            }}
          />
        );

      },
    })),
  ], [ staffQuery.data!.classes, formattedStaff ]);


  const updateTeacherClasses = (teacherId: number, checking: boolean) => {
    const allClassIds = (staffQuery.data?.classes ?? []).map((c: StaffClass) => c.id);

    let classIdsToAdd: number[] = [];

    updateStaffPropsById(teacherId, (teacherRow) => {
      teacherRow.allNone.loading = true;

      if (checking) {
        classIdsToAdd = allClassIds.filter((classId) => {
          const permission = teacherRow.classes[classId]?.permission ?? 'none';
          return permission === 'none';
        });

        classIdsToAdd.forEach((classId) => {
          if (!teacherRow.classes[classId]) {
            teacherRow.classes[classId] = {
              permission: 'none',
              loading: true,
            };
          } else {
            teacherRow.classes[classId].loading = true;
          }
        });
      } else {
        allClassIds.forEach((classId) => {
          if (!teacherRow.classes[classId]) {
            teacherRow.classes[classId] = {
              permission: 'none',
              loading: true,
            };
          } else {
            teacherRow.classes[classId].loading = true;
          }
        });
      }
    });

    if (checking) {
      if (!classIdsToAdd.length) {
        updateStaffPropsById(teacherId, (teacherRow) => {
          teacherRow.allNone.loading = false;

          allClassIds.forEach((classId) => {
            if (teacherRow.classes[classId]) {
              teacherRow.classes[classId].loading = false;
            }
          });
        });
        return;
      }

      mutations.teacherClassesAdd.mutate({
        teacherId,
        classesIds: classIdsToAdd,
      });
    } else {
      mutations.teacherClassesDelete.mutate({
        teacherId,
      });
    }
  };

  const updateIsAdmin = (teacherId: number, newAdminValue: boolean) => {

    if (teacherId === me?.id) {
      // Prevent self being able to alter their own admin rights
      return;
    }

    updateStaffPropsById(
      teacherId,
      (teacher) => {
        teacher.admin.loading = true;
      },
    );

    mutations.isAdmin.mutate({ teacherId, newAdminValue });
  };



  const formatData = (queryData: StaffGet): StaffTableItem[] | null => {
    if (queryData.teachers.length === 0) {
      return null;
    }
    return queryData.teachers.map((teacher: StaffTeacher) => {
      const classes = queryData.classes.reduce((acc, classObj: StaffClass) => {

        const teacherClass = queryData.teachersClasses.find(
          (tc: StaffTeacherClass) => tc.teacherId == teacher.id && tc.classId == classObj.id,
        );

        const getSubscribed = (val: any): boolean => {
          if (!val) return false;
          if (val.type === 'Buffer') return val.data[0] === 1;
          return Boolean(val);
        };

        const permission: ClassPermission =
  !teacherClass
    ? 'none'
    : (getSubscribed(teacherClass.isSubscribedToWeeklyUpdates) ? 'email' : 'view');

        acc[classObj.id] = {
          permission,
          loading: false,
        };

        return acc;
      }, {} as StaffTableItem['classes']);

      const [ phoneNationalCode, phoneNumber ] = splitPhone(teacher.phone);

      const onEditParams: DataToEdit = {
        firstName: teacher.firstName,
        lastName: teacher.lastName,
        id: teacher.id,
        email: teacher.email,
        username: teacher.username,
        recoveryEmail: teacher.recoveryEmail,
        jobType: teacher.jobType,
        jobTitle: teacher.jobTitle,
        phoneNationalCode: phoneNationalCode,
        phoneNumber: phoneNumber,
      };

      return {
        ...teacher,
        allNone: {
          checked: teacher.classesIds.length === queryData.classes.length,
          loading: false,
        },
        admin: {
          checked: teacher.isAdmin,
          loading: false,
        },
        emailSubscribe: {
          checked: teacher.isSubscribedToWeeklyUpdates,
          loading: false,
        },
        classes,
        onEditParams,
        updatedAt: DateTime.now().toISO({ includeOffset: false }),
      };
    });
  };

  if (!staffQuery.isFetched || staffQuery.isFetching || staffQuery.isRefetching || formattedStaff === null) {
    return (<Loader />);
  }

  const headerHeight = translate('pages.ROSTER_MANAGEMENT.STAFF.EMAIL_SUBSCRIBE').length;



  return (
    <StyledWrapper className={'bg-white rounded-default shadow p-md'}>
      <GlobalStyle $height={headerHeight} />
      {confirmWeeklySubscriptionModal}
      <Table
        id={'staff'}
        sticky
        truncate
        cols={columns}
        content={formattedStaff}
        initialState={{
          sortBy: [
            {
              id: 'name',
              desc: false,
            },
          ],
        }}
        disableSortRemove
        autoResetSortBy={false}
        newId={newStaff ?? undefined}
      />
      <LegendBar>
        <LegendWrap>
          <LegendItem>
            <EyeIcon active={false} />
            <LegendText>
              {translate('pages.ROSTER_MANAGEMENT.STAFF.EMAIL_SUBSCRIBE_MODAL.CANT_VIEW').toString()}
            </LegendText>
          </LegendItem>

          <LegendItem>
            <EyeIcon active={true} />
            <LegendText>
              {translate('pages.ROSTER_MANAGEMENT.STAFF.EMAIL_SUBSCRIBE_MODAL.CAN_VIEW').toString()}
            </LegendText>
          </LegendItem>

          <LegendItem>
            <MailIcon />
            <LegendText>
              {translate('pages.ROSTER_MANAGEMENT.STAFF.EMAIL_SUBSCRIBE_MODAL.VIEWWITHEMAILTIP').toString()}
            </LegendText>
          </LegendItem>
        </LegendWrap>
      </LegendBar>
    </StyledWrapper>
  );
};

const StyledWrapper = styled('div')`
  max-width: initial;
  width: fit-content;
`;

const ConfirmWeeklySubscription = ({ classCount, toggle, confirm }: ConfirmWeeklySubscriptionProps) => {
  const { translate } = useI18n();
  return (
    <>
      <div className={'container'}>
        <Translate i18nKey={'pages.ROSTER_MANAGEMENT.STAFF.EMAIL_SUBSCRIBE_MODAL.MESSAGE'} values={{ count: classCount }}>
          { /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
          /* @ts-ignore */ }
          <strong />
        </Translate>
      </div>
      <ModalFooter>
        <CancelButtonModal onClick={() => toggle(false)}></CancelButtonModal>
        <Button
          isOutline
          color={'positive'}
          onClick={confirm}
        >{translate('components.BUTTONS.OK')}</Button>
      </ModalFooter>
    </>
  );
};

export default StaffTable;

interface StaffTableProps {
  onEdit: ((data: DataToEdit) => void) | null;
  staffQuery:  QueryObserverResult<StaffGet, unknown>;
  newStaff: number | null;
}

interface ConfirmWeeklySubscriptionProps extends Pick<ToggleModalProps, 'toggle'> {
  classCount: number;
  confirm: () => void;
}

interface StaffTableItem extends StaffTeacher {
  allNone: {
    loading: boolean;
  },
  id: number;
  firstName: string;
  lastName: string | null;
  admin: {
    checked: boolean;
    loading: boolean;
  };
  emailSubscribe: {
    checked: boolean;
    loading: boolean;
  };
  classes: {
    [id: number]: {
      permission: ClassPermission;
      loading: boolean;
    };
  };
  onEditParams: DataToEdit;
  updatedAt: string;
}
